// Interceptor global de fetch — refresh automático de token JWT
// Cubre los 54+ componentes que usan fetch nativo sin pasar por axios.
// Instalado en index.js antes del render de React.
import { debeConvertir, formDataAJson } from './formDataJson';

let isRefreshing = false;
let pendingQueue = [];

function notifyAll(token) {
  pendingQueue.forEach(({ resolve }) => resolve(token));
  pendingQueue = [];
}

function rejectAll(error) {
  pendingQueue.forEach(({ reject }) => reject(error));
  pendingQueue = [];
}

function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('usuario');
  window.location.href = window.location.origin + '/#/login';
}

// Error de refresh que sí significa "la sesión no sirve más": sin refresh token,
// o el backend lo rechazó (400/401). Una caída de red, un 5xx, un 429 o una
// respuesta que no es JSON (el servidor despertando, la página anti-bots del
// hosting) no cierran la sesión: se reintenta con el próximo pedido.
function errorDefinitivo(mensaje) {
  const e = new Error(mensaje);
  e.definitivo = true;
  return e;
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw errorDefinitivo('Sin refresh token');

  const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
  const res = await window._originalFetch(`${API}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',   // renueva la cookie de /api/uploads
    body: JSON.stringify({ refreshToken }),
  });

  if (res.status === 400 || res.status === 401) throw errorDefinitivo('Refresh rechazado');
  if (!res.ok) throw new Error(`Refresh fallido (${res.status})`);
  const data = await res.json();
  if (!data?.token) throw new Error('Refresh sin token');
  return data;
}

// Normaliza cualquier variante de headers a un objeto plano
function normalizeHeaders(headers) {
  if (!headers) return {};
  if (headers instanceof Headers) return Object.fromEntries(headers.entries());
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...headers };
}

export function installFetchInterceptor() {
  if (window._fetchInterceptorInstalled) return;
  window._fetchInterceptorInstalled = true;
  window._originalFetch = window.fetch.bind(window);

  window.fetch = async function interceptedFetch(resource, options = {}) {
    const urlStr = typeof resource === 'string' ? resource : resource?.url || resource?.toString?.() || '';

    // No interceptar llamadas fuera de /api/ ni los endpoints de autenticación
    const SKIP_PATTERNS = ['/auth/refresh', '/login', '/forgot-password', '/reset-password'];
    const isApi = urlStr.includes('/api/');
    const isAuth = SKIP_PATTERNS.some(p => urlStr.includes(p));

    if (!isApi || isAuth) {
      return window._originalFetch(resource, options);
    }

    // Inyectar token actual si no viene en los headers
    const headers = normalizeHeaders(options.headers);
    if (!headers.Authorization && !headers.authorization) {
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const patchedOptions = { ...options, headers };

    // El hosting corta los POST multipart: el FormData viaja como JSON (utils/formDataJson)
    if (debeConvertir(urlStr, options.body)) {
      patchedOptions.body = JSON.stringify(await formDataAJson(options.body));
      Object.keys(headers).filter(h => h.toLowerCase() === 'content-type').forEach(h => delete headers[h]);
      headers['Content-Type'] = 'application/json';
    }

    const response = await window._originalFetch(resource, patchedOptions);

    // Solo procesar 401 con code TOKEN_EXPIRED
    if (response.status !== 401) return response;

    let data = {};
    try { data = await response.clone().json(); } catch {}
    // Sesión revocada (salió en otro lado, un admin la cerró o lo dieron de
    // baja): no hay nada que renovar, al login.
    if (data.code === 'SESSION_CLOSED' || data.code === 'USUARIO_INACTIVO') {
      forceLogout();
      return response;
    }
    if (data.code !== 'TOKEN_EXPIRED') return response;

    // Si ya hay un refresh en curso, encolar esta request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then(newToken => {
        const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
        return window._originalFetch(resource, { ...patchedOptions, headers: retryHeaders });
      }).catch(() => response);
    }

    // Iniciar refresh
    isRefreshing = true;
    try {
      const refreshData = await refreshAccessToken();

      localStorage.setItem('token', refreshData.token);
      if (refreshData.user) localStorage.setItem('usuario', JSON.stringify(refreshData.user));

      // Notificar a AuthContext (vía window y vía api._onTokenRefreshed)
      if (typeof window.__onTokenRefreshed === 'function') {
        window.__onTokenRefreshed(refreshData.token, refreshData.user);
      }

      notifyAll(refreshData.token);

      // Reintentar la request original con el nuevo token
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshData.token}` };
      return window._originalFetch(resource, { ...patchedOptions, headers: retryHeaders });

    } catch (err) {
      rejectAll(err);
      if (err?.definitivo) forceLogout();
      return response;
    } finally {
      isRefreshing = false;
    }
  };
}
